/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF CONDITIONS OF ANY KIND, either express or implied. See the
  License for the specific language governing permissions and limitations under
  the License.
*/

import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Divider from '@material-ui/core/Divider';
import Avatar from '@material-ui/core/Avatar';
import Fab from '@material-ui/core/Fab';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import Menu from '@material-ui/core/Menu';
import { usePopupState, bindMenu, bindTrigger, bindToggle } from 'material-ui-popup-state/hooks';
import IconAccount from 'mdi-material-ui/Account';
import IconAccountOutline from 'mdi-material-ui/AccountOutline';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  List,
  Radio,
  RadioGroup,
} from '@material-ui/core';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import compose from 'lodash/fp/compose';
import noop from 'lodash/noop';
import { getInitials } from '../../utils';
import { Elang } from '../../locales';
import { FeedbackDialog } from '../FeedbackDialog';

import { ExpansionIcon, TranslationService, IUser } from '@marapp/earth-shared';
import useTheme from '@material-ui/core/styles/useTheme';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    display: 'flex',
    alignItems: 'center',
    right: theme.spacing(2.5), // Changed from 0 to 32px from right edge
    top: theme.spacing(2), // Changed from 0 to 16px from top edge
    zIndex: 10,
    padding: 0, // Remove padding since we're positioning directly
  },
  authButton: {
    height: theme.spacing(4),
    minHeight: theme.spacing(4),
    width: theme.spacing(4),
    // Add better visibility and interaction styles
    boxShadow: theme.shadows[2],
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
    '&:focus': {
      boxShadow: theme.shadows[6],
    },
  },
  menu: {
    backgroundColor: theme.palette.grey['600'],
    minWidth: theme.spacing(28),
    maxWidth: theme.spacing(40),
    zIndex: 1300,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  avatar: {
    // Remove background color override - let CSS handle it
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),
  },
  signOut: {
    textTransform: 'lowercase',
  },
}));

const languageLabels = {
  [Elang.EN]: 'English',
  //[Elang.EO]: 'Esperanto',
  [Elang.ES]: 'Español',
  [Elang.FR]: 'Français',
  [Elang.PT]: 'Português',
  //[Elang.RU]: 'Pусский',
};

interface IProps {
  profileLinkProps: any;
  userData?: IUser;
  onLogin?: () => void;
  onLogout?: () => void;
  onSignUp?: () => void;
  onSubmitFeedback?: (values: { subject: string; message: string }) => Promise<any>;
  isAuthenticated?: boolean;
  selected?: string;
  // Update workspace props to use array format
  groups?: string[];
  roles?: any;
  selectedWorkspace?: string[]; // Changed from string to string[]
  availableGroups?: any[];
  onWorkspaceChange?: (workspace: string) => void; // Still takes single workspace string
  MAP_ADMIN_URL?: string;
  checkAdminRole?: (roleArray: string[]) => boolean;
}

export const UserMenu = (props: IProps) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  // Add this flag to disable avatar images for testing
  const DISABLE_AVATAR_IMAGES = false; // Set to false to re-enable images

  // Get the current language from i18n
  const selectedLanguage = i18n.language;

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const {
    isAuthenticated = false,
    onSubmitFeedback,
    onLogin = noop,
    onLogout = noop,
    onSignUp = noop,
    selected,
    profileLinkProps,
    userData,
    groups = [],
    roles = {},
    selectedWorkspace,
    availableGroups = [],
    onWorkspaceChange = noop,
    MAP_ADMIN_URL = '',
    checkAdminRole = () => false,
  } = props;

  // Fix: Handle both string and array formats for selectedWorkspace
  const normalizedSelectedWorkspace = useMemo(() => {
    if (Array.isArray(selectedWorkspace)) {
      return selectedWorkspace[0] || '';
    }
    return selectedWorkspace || '';
  }, [selectedWorkspace]);

  const popupState = usePopupState({ variant: 'popover', popupId: 'userMenu' });
  const popupStateLang = usePopupState({ variant: 'popover', popupId: 'languageMenu' });
  const popupStateWorkspace = usePopupState({ variant: 'popover', popupId: 'workspaceMenu' });

  // Fix: Only initialize workspace open state once, don't change it on every render
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(true);

  const handleLogout = () => {
    const defaultLanguage = TranslationService.getDefaultLanguage();
    i18n.changeLanguage(defaultLanguage);
    popupState.close();
    onLogout();
  };

  const handleMenuItemClick = (callback?: () => void) => {
    return () => {
      if (callback) callback();
      popupState.close();
    };
  };

  // Fix: Memoize user display name calculation
  const getUserDisplayName = useMemo(() => {
    if (userData?.name) return userData.name;
    if (userData?.firstName && userData?.lastName) {
      return `${userData.firstName} ${userData.lastName}`;
    }
    return userData?.email || '';
  }, [userData?.name, userData?.firstName, userData?.lastName, userData?.email]);

  // Fix: Memoize initials calculation
  const initials = useMemo(() => {
    return getUserDisplayName
      ? getInitials(getUserDisplayName)
      : getInitials(userData?.email || '');
  }, [getUserDisplayName, userData?.email]);

  const userPicture = DISABLE_AVATAR_IMAGES ? null : userData?.picture;

  // Collapse the language and workspace options when hiding the main menu
  useEffect(() => {
    if (!popupState.isOpen) {
      popupStateLang.setOpen(false);
      // Don't reset workspace open state when menu closes
    }
  }, [popupState.isOpen, popupStateLang]);

  const handleWorkspaceChange = useCallback(
    (workspace: string) => {
      onWorkspaceChange(workspace);
      popupStateWorkspace.close();
    },
    [onWorkspaceChange, popupStateWorkspace]
  );

  // Fix: Memoize admin organizations to prevent recalculation
  const adminOrgs = useMemo(() => {
    return Object.keys(roles).filter((group) => checkAdminRole(roles[group]));
  }, [roles, checkAdminRole]);

  const superAdminGroup = useMemo(() => {
    return adminOrgs.find((group) => group === '*');
  }, [adminOrgs]);

  // Fix: Memoize menu groups to prevent recreation
  const menuGroups = useMemo(() => {
    const groups = [...availableGroups];
    if (superAdminGroup) {
      groups.push({
        name: superAdminGroup,
        description: superAdminGroup,
      });
    }
    return groups;
  }, [availableGroups, superAdminGroup]);

  // Fix: Memoize filtered menu groups
  const filteredMenuGroups = useMemo(() => {
    return menuGroups.filter((g) => g.name !== 'common');
  }, [menuGroups]);

  // Get the primary workspace - use normalized value
  const primaryWorkspace = normalizedSelectedWorkspace;

  // Fix: Memoize available workspace names
  const availableWorkspaceNames = useMemo(() => {
    const names = (
      filteredMenuGroups.length > 0 ? filteredMenuGroups : groups.map((g) => ({ name: g }))
    ).map((g) => (typeof g === 'string' ? g : g.name));

    return names;
  }, [filteredMenuGroups, groups]);

  // Fix: Simplify actualSelectedValue calculation and memoize it
  const actualSelectedValue = useMemo(() => {
    return primaryWorkspace || '';
  }, [primaryWorkspace]);

  // Show workspace section if authenticated - always show to indicate workspace functionality exists
  const shouldShowWorkspace = isAuthenticated;

  // Fix: Memoize workspace section to prevent recreation
  const workspaceSection = useMemo(() => {
    if (!shouldShowWorkspace) return [];

    return [
      <MenuItem
        key="workspace"
        onClick={() => {
          setIsWorkspaceOpen(!isWorkspaceOpen);
        }}
      >
        <ListItemText
          primary={t('Workspace')}
          secondary={primaryWorkspace || t('No workspace selected')}
        />
        <ExpansionIcon isActive={isWorkspaceOpen} />
      </MenuItem>,
      <Collapse key="workspace-collapse" in={isWorkspaceOpen}>
        <div style={{ padding: '0 16px' }}>
          {filteredMenuGroups.length === 0 && groups.length === 0 ? (
            <Typography
              variant="body2"
              style={{ padding: '8px 0', color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {t('No workspaces available')}
            </Typography>
          ) : (
            <FormControl component="fieldset" style={{ width: '100%' }}>
              <RadioGroup
                value={actualSelectedValue}
                onChange={(event) => {
                  handleWorkspaceChange(event.target.value);
                }}
              >
                {(filteredMenuGroups.length > 0
                  ? filteredMenuGroups
                  : groups.map((g) => ({ name: g }))
                ).map((g) => {
                  const groupName = typeof g === 'string' ? g : g.name;
                  const hasAdminAccess = !!adminOrgs.find((group) => group === groupName);

                  return (
                    <Box
                      key={groupName}
                      display="flex"
                      alignItems="center"
                      width="100%"
                      style={{ margin: '4px 0' }}
                    >
                      <FormControlLabel
                        value={groupName}
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            checked={groupName === actualSelectedValue}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {typeof g === 'object' && g.description ? g.description : groupName}
                          </Typography>
                        }
                        style={{
                          flex: 1,
                          margin: 0,
                          marginRight: hasAdminAccess ? 8 : 0,
                        }}
                      />
                      {hasAdminAccess && MAP_ADMIN_URL && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${MAP_ADMIN_URL}${groupName}`, '_blank');
                          }}
                          style={{
                            fontSize: '0.7rem',
                            padding: '2px 6px',
                            minWidth: 'auto',
                          }}
                        >
                          {t('Admin')}
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </RadioGroup>
            </FormControl>
          )}
        </div>
      </Collapse>,
    ];
  }, [
    shouldShowWorkspace,
    isWorkspaceOpen,
    t,
    primaryWorkspace,
    filteredMenuGroups,
    groups,
    actualSelectedValue,
    handleWorkspaceChange,
    adminOrgs,
    MAP_ADMIN_URL,
  ]);

  // Remove the debug console.logs that are causing frequent re-renders
  // Keep only essential logging if needed for debugging
  // console.log statements should be removed in production

  return (
    <>
      <div className={`marapp-qa-useraccount ${classes.root}`}>
        <div>
          <Fab
            className={`${classes.authButton} marapp-qa-user-menu-button`}
            size="small"
            color="primary"
            {...bindTrigger(popupState)}
          >
            {userPicture ? (
              <Avatar
                src={userPicture}
                alt={userData?.name || 'User'}
                className={`${classes.avatar}`}
              >
                {initials}
              </Avatar>
            ) : initials ? (
              <Avatar
                className={`${classes.avatar} avatar-fallback`}
                alt={userData?.name || 'User'}
              >
                {initials}
              </Avatar>
            ) : isAuthenticated ? (
              <IconAccount />
            ) : (
              <IconAccountOutline />
            )}
          </Fab>

          <Menu
            classes={{ paper: classes.menu }}
            {...bindMenu(popupState)}
            onClose={popupState.close}
            disableAutoFocusItem
            keepMounted={false}
          >
            {[
              ...(isAuthenticated
                ? [
                    <ListItem key="user-details">
                      <ListItemAvatar>
                        <Avatar
                          className={`${classes.avatar} avatar-fallback`}
                          src={userPicture}
                          alt={userData?.name || 'User'}
                        >
                          {initials}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" noWrap title={userData?.email}>
                            {userData?.email}
                          </Typography>
                        }
                        secondary={
                          <Link className={classes.signOut} onClick={handleLogout}>
                            {t('Sign Out')}
                          </Link>
                        }
                      />
                    </ListItem>,
                    <MenuItem
                      key="profile"
                      divider
                      selected={selected === 'profile'}
                      onClick={handleMenuItemClick(profileLinkProps?.onClick)}
                      {...(profileLinkProps
                        ? { component: profileLinkProps.component, to: profileLinkProps.to }
                        : {})}
                    >
                      {t('Profile')}
                    </MenuItem>,
                  ]
                : [
                    <MenuItem key="login" onClick={handleMenuItemClick(onLogin)}>
                      {t('Sign in')}
                    </MenuItem>,
                  ]),
              ...workspaceSection,
              ...(shouldShowWorkspace ? [<Divider key="workspace-divider" />] : []),
              <MenuItem key="language" {...bindToggle(popupStateLang)}>
                <ListItemText
                  primary={t('Language')}
                  secondary={languageLabels[selectedLanguage]}
                />
                <ExpansionIcon isActive={popupStateLang.isOpen} />
              </MenuItem>,
              <Collapse key="language-collapse" in={popupStateLang.isOpen}>
                <MenuList>
                  {Object.keys(languageLabels).map((key) => (
                    <MenuItem
                      key={key}
                      onClick={() => {
                        i18n.changeLanguage(key);
                        popupState.close();
                      }}
                      selected={selectedLanguage === key}
                      className={classes.nested}
                    >
                      {languageLabels[key]}
                    </MenuItem>
                  ))}
                </MenuList>
              </Collapse>,
              ...(isAuthenticated
                ? [
                    <Divider key="divider" />,
                    <MenuItem
                      key="feedback"
                      onClick={handleMenuItemClick(() => setIsFeedbackOpen(true))}
                    >
                      {t('Send feedback')}
                    </MenuItem>,
                  ]
                : []),
            ]}
          </Menu>
        </div>
      </div>

      <FeedbackDialog
        open={isFeedbackOpen}
        onCancel={() => setIsFeedbackOpen(false)}
        onSubmitFeedback={onSubmitFeedback}
      />
    </>
  );
};
